<template>
	<div class="d-flex flex-column flex h100">
		<navbar></navbar>
		<div class="app">
			<config-sidenav></config-sidenav>
			<div id="content" class="app-content box-shadow-3">
				<div class="navbar navbar-expand-lg">
					<!-- Page title -->
					<loading type="header" v-if="editUser.loaded == 0" />
					<div v-if="editUser.loaded != 0" class="navbar-text nav-title flex" id="pageTitle">
						<i
							class="fas fa-fw fa-lock-alt"
							v-if="!enoughAccess"
							v-tippy="{ placement: 'bottom-start' }"
							title="This user has a higher access level than your own, and cannot be edited"
						></i>
						{{ editUser.first_name }} {{ editUser.last_name }} ({{ editUser.scorer_id }})
					</div>
				</div>

				<div class="content-main" style="background-color:white">
					<!-- Page content goes here -->
					<div class="h-100" v-if="editUser.loaded != 0">
						<div class="d-sm-flex b-t h-100">
							<edit-pane :cancel="loadData" :save="saveUser" :dirty="dirty" :valid="valid">
								<div class="p-4 b-b _600 nav-title">User Details</div>
								<div class="col-sm-10 col-12">
									<div class="row m-0 pt-4">
										<div class="col-sm-6 col-12">
											<div class="form-group" :class="{ invalid: !valid.field('name') }">
												<label>First Name</label>
												<input
													type="text"
													class="form-control"
													v-model="editUser.first_name"
													:disabled="!enoughAccess"
												/>
											</div>
										</div>
										<div class="col-sm-6 col-12">
											<div class="form-group">
												<label>Last Name</label>
												<input
													type="text"
													class="form-control"
													v-model="editUser.last_name"
													:disabled="!enoughAccess"
												/>
											</div>
										</div>
										<div class="col-sm-6 col-12">
											<div class="form-group">
												<label>Scorer ID</label>
												<input
													type="text"
													class="form-control"
													v-model.trim="editUser.scorer_id"
													:disabled="!enoughAccess"
												/>
											</div>
										</div>
										<div class="col-sm-6 col-12">
											<div class="form-group" :class="{ invalid: !valid.field('email') }">
												<label>Email</label>
												<input
													type="text"
													class="form-control"
													v-model.trim="editUser.email"
													:disabled="!enoughAccess"
												/>
											</div>
										</div>
										<div
											class="form-group col-sm-6 col-12"
											:class="{ invalid: !valid.field('role') }"
										>
											<label>Role</label>
											<v-select
												:options="accessibleRoles"
												v-model="sRole"
												label="name"
												:disabled="!enoughAccess"
											></v-select>
										</div>
										<div class="form-group col-sm-6 col-12">
											<label>Collaborative Scoring Team</label>
											<config-select
												:options="teams"
												v-model="editUser.team_id"
												byField="id"
												label="name"
												:nullOption="true"
												nullText="None"
												:disabled="!enoughAccess"
											></config-select>
										</div>
										<div class="form-group col-sm-6 col-12">
											<label>Preferred Content Areas</label>
											<v-select
												:options="areas"
												v-model="sAreas"
												multiple
												label="name"
												:disabled="!enoughAccess"
											></v-select>
										</div>
										<div class="form-group col-sm-6 col-12">
											<label>Preferred Grades</label>
											<v-select
												:options="grades"
												v-model="sGrades"
												multiple
												label="name"
												:disabled="!enoughAccess"
											></v-select>
										</div>
										<div
											class="form-group col-sm-6 col-12"
											:class="{ invalid: !valid.field('district') }"
										>
											<label>District</label>
											<v-select
												:options="districts"
												v-model="sDistrict"
												label="name"
												:disabled="!enoughAccess"
											></v-select>
										</div>
										<div
											class="form-group col-sm-6 col-12"
											:class="{ invalid: !valid.field('school') }"
										>
											<label>School</label>
											<v-select
												:options="schools"
												v-model="sSchool"
												label="name"
												:disabled="!enoughAccess"
											></v-select>
										</div>
										<div class="col-6 col-sm-3 p-0">
											<div class="form-group col-12">Active</div>
											<div class="form-group col-12">
												<label class="ui-switch ui-switch-md theme-accent">
													<input
														type="checkbox"
														v-model="editUser.active"
														:disabled="!enoughAccess"
													/>
													<i></i>
												</label>
											</div>
										</div>
										<div class="col-6 col-sm-3 p-0">
											<div class="form-group col-12">Archived</div>
											<div class="form-group col-12">
												<label class="ui-switch ui-switch-md theme-accent">
													<input
														type="checkbox"
														v-model="editUser.archived"
														:disabled="!enoughAccess"
													/>
													<i></i>
												</label>
											</div>
										</div>
										<div class="col-12 col-sm-6 form-group align-self-end" v-if="enoughAccess">
											<button class="btn theme-accent btn-block" @click="togglePass">
												Set Password
												<i
													class="far fa-fw fa-angle-up anim-rotate"
													:class="{ 'rotate-180': editUser.setPass }"
												></i>
											</button>
										</div>
										<div class="col-12 col-sm-6"></div>
										<div class="row m-0 w-100" v-show="editUser.setPass">
											<div
												class="col-12 col-sm-6 form-group"
												:class="{ invalid: !valid.field('password') }"
											>
												<label>New Password</label>
												<input
													type="password"
													class="form-control"
													v-model.trim="editUser.password"
													autocomplete="new-password"
												/>
											</div>
											<div
												class="col-12 col-sm-6 form-group d-flex flex-column justify-content-end"
											>
												<div
													class="mt-1 mb-0 text-xs text-muted"
													v-for="error in valid.errors('password')"
													:key="error"
													style="min-height: 28px;"
												>
													{{ error }}
												</div>
											</div>
											<div
												class="col-12 col-sm-6 form-group"
												:class="{ invalid: !valid.field('passwordMatch') }"
											>
												<label>Confirm Password</label>
												<input
													type="password"
													class="form-control"
													v-model.trim="editUser.password2"
													autocomplete="new-password"
												/>
											</div>
											<div
												class="col-12 col-sm-6 form-group d-flex flex-column justify-content-end"
											>
												<div
													class="mt-1 mb-0 text-xs text-muted"
													v-for="error in valid.errors('passwordMatch')"
													:key="error"
													style="min-height: 28px;"
												>
													{{ error }}
												</div>
											</div>
										</div>
									</div>
								</div>
							</edit-pane>
							<save-optional-modal
								:dirty="dirty"
								:valid="valid"
								:save="saveUser"
								:next="saveOptNext"
								:cancel="saveOptCancel"
								objectText="User"
								:objectName="user.first_name + ' ' + user.last_name"
								actionText="leave the page"
								v-model="saveOptModal"
							/>
						</div>
					</div>
				</div>
			</div>
		</div>
		<mzfooter></mzfooter>
	</div>
</template>

<style scoped>
.margin-overlap {
	margin-right: -2.5rem;
}
</style>

<script>
import EditPane from "@/components/EditPane";
import SaveOptionalModal from "@/components/SaveOptionalModal";
import ConfigSelect from "@/components/ConfigSelect";

import UserService from "@/services/UserService";
import TenantService from "@/services/TenantService";
import SchoolService from "@/services/SchoolService";
import TeamService from "@/services/TeamService";
import ContentAreaService from "@/services/ContentAreaService";
import GradeService from "@/services/GradeService";
import DistrictService from "@/services/DistrictService";
import ValidationService from "@/services/ValidationService";
import notie from "@/services/NotieService";
import Utils from "@/services/Utils";
import BB from "bluebird";

export default {
	name: "UserEdit",

	props: ["user", "params", "query"],

	components: { EditPane, SaveOptionalModal, ConfigSelect },

	data() {
		return {
			tab: 1,
			editUser: {
				team_ids: [],
				role_id: null,
				role: null,
				setPass: false,
				password: null,
				password2: null,
				loaded: 0
			},
			teams: [],
			roles: [],
			sRole: null,
			areas: [],
			sAreas: null,
			grades: [],
			sGrades: null,
			districts: [],
			sDistrict: null,
			schools: null,
			sSchool: null,
			dirty: false,
			valid: {},
			saveOptModal: false,
			saveOptNext: () => {},
			saveOptCancel: () => {}
		};
	},

	computed: {
		accessibleRoles() {
			return _.filter(this.roles, role => {
				return this.user.role.access >= role.access;
			});
		},
		enoughAccess() {
			if (!(this.user.role && this.editUser.role)) {
				return true;
			}
			return this.user.role.access >= this.editUser.role.access;
		},
		sAreaIDs() {
			return _.map(this.sAreas, "id");
		},
		sGradeIDs() {
			return _.map(this.sGrades, "id");
		},
		sDistrictID() {
			return (this.sDistrict && this.sDistrict.id) || null;
		},
		sSchoolID() {
			return (this.sSchool && this.sSchool.id) || null;
		}
	},

	watch: {
		sRole(newVal, oldVal) {
			if (!newVal) {
				this.editUser.role_id = null;
				this.editUser.role = null;
			} else if (this.editUser.role_id != newVal.id) {
				this.editUser.role_id = newVal.id;
				this.editUser.role = newVal;
			}
		},
		sAreaIDs(newVal) {
			let oldVal = this.editUser.content_area_ids;
			if (oldVal == null) {
				oldVal = newVal.slice(0, newVal.length - 1);
			}
			let same = true;
			if (newVal.length == oldVal.length) {
				for (let i = 0; i < newVal.length; i++) {
					if (newVal[i] != oldVal[i]) {
						console.log("ca", newVal[i], "not equal", oldVal[i]);
						same = false;
					}
				}
			} else {
				same = false;
			}

			if (!same) {
				this.editUser.content_area_ids = newVal;
			}
		},
		sGradeIDs(newVal) {
			let oldVal = this.editUser.grade_ids;
			if (oldVal == null) {
				oldVal = newVal.slice(0, newVal.length - 1);
			}
			let same = true;
			if (newVal.length == oldVal.length) {
				for (let i = 0; i < newVal.length; i++) {
					if (newVal[i] != oldVal[i]) {
						console.log("grade", newVal[i], "not equal", oldVal[i]);
						same = false;
					}
				}
			} else {
				same = false;
			}

			if (!same) {
				this.editUser.grade_ids = newVal;
			}
		},
		sDistrictID(newVal) {
			let oldVal = this.editUser.district_id;
			if (newVal != oldVal) {
				this.editUser.district_id = newVal;
			}

			SchoolService.listSchools({
				district_id: this.editUser.district_id
			}).then(r => {
				this.schools = r.data.schools;

				let matchSchool = _.find(this.schools, {
					id: this.editUser.school_id
				});
				this.sSchool = matchSchool;
			});
		},
		sSchoolID(newVal) {
			let oldVal = this.editUser.district_id;
			if (newVal != oldVal) {
				this.editUser.school_id = newVal;
			}
		}
	},

	created() {
		this.loadData();
		this.initValidation();

		if (this.query.tab) {
			this.tab = this.query.tab;
		}
	},

	beforeRouteLeave(to, from, next) {
		if (this.dirty) {
			this.saveOptNext = () => {
				next();
			};
			this.saveOptCancel = () => {
				next(false);
			};
			this.saveOptModal = true;
		} else {
			next();
		}
	},

	methods: {
		initValidation() {
			this.valid = ValidationService.newValidator({
				name: {
					group: "details",
					errorMsg: "You must assign a first name",
					func: () => {
						return this.editUser && this.editUser.first_name && this.editUser.first_name != "";
					}
				},
				role: {
					group: "details",
					errorMsg: "You must choose a role",
					func: () => {
						return this.editUser && this.editUser.role_id;
					}
				},
				district: {
					group: "details",
					errorMsg: "You must choose a district",
					func: () => {
						return this.editUser && this.editUser.district_id;
					}
				},
				school: {
					group: "details",
					errorMsg: "You must choose a school",
					func: () => {
						return this.editUser && this.editUser.school_id;
					}
				},
				email: {
					group: "details",
					errorMsg: "You must enter a valid email",
					func: () => {
						let re = /\S+@\S+\.\S+/;
						return this.editUser && this.editUser.email && re.test(this.editUser.email);
					}
				},
				password: {
					group: "details",
					errorMsg: "The new password must be at least 5 characters long",
					func: () => {
						return this.editUser && (this.editUser.password == null || this.editUser.password.length >= 5);
					}
				},
				passwordMatch: {
					group: "details",
					errorMsg: "The new passwords must match",
					func: () => {
						return (
							this.editUser &&
							(this.editUser.password == null || this.editUser.password == this.editUser.password2)
						);
					}
				}
			});
		},

		blankUser() {
			return {
				team_id: null,
				role_id: null,
				role: null,
				setPass: false,
				password: null,
				password2: null,
				active: true
			};
		},

		watchChanges() {
			if (this.unwatch) {
				this.unwatch();
			}
			this.unwatch = this.$watch(
				"editUser",
				(newc, old) => {
					console.log(newc);
					console.log("marking dirty");
					this.dirty = true;
				},
				{ deep: true }
			);
		},

		loadData() {
			this.dirty = false;
			var loaded = this.editUser.loaded;
			loaded++;

			if (this.params.id != "new") {
				return BB.props({
					user: UserService.getUser(this.params.id),
					client: TenantService.getClient("current"),
					teams: TeamService.listTeams(),
					contentAreas: ContentAreaService.listContentAreas(),
					grades: GradeService.listGrades(),
					districts: DistrictService.listDistricts({ noEmpty: true })
				})
					.then(resps => {
						this.editUser = resps.user.data;
						this.editUser.loaded = loaded;

						this.roles = resps.client.data.roles;
						if (this.roles.length > 0 && this.editUser) {
							if (this.editUser.role) {
								this.sRole = _.find(this.roles, {
									id: this.editUser.role.id
								});
							} else {
								this.sRole = null;
							}
						}

						this.teams = resps.teams.data.teams;

						this.sAreas = [];
						this.areas = resps.contentAreas.data.content_areas;
						if (this.areas && this.areas.length > 0 && this.editUser) {
							_.each(this.editUser.content_area_ids, caid => {
								let area = _.find(this.areas, {
									id: caid
								});
								if (area) {
									this.sAreas.push(area);
								}
							});
						}

						this.sGrades = [];
						this.grades = resps.grades.data;
						if (this.grades && this.grades.length > 0 && this.editUser) {
							_.each(this.editUser.grade_ids, gid => {
								let grade = _.find(this.grades, {
									id: gid
								});
								if (grade) {
									this.sGrades.push(grade);
								}
							});
						}

						this.districts = resps.districts.data.districts;
						if (this.districts && this.districts.length > 0 && this.editUser) {
							if (this.editUser.district_id) {
								this.sDistrict = _.find(this.districts, {
									id: this.editUser.district_id
								});
							} else {
								this.sDistrict = null;
							}
						}

						this.watchChanges();
					})
					.catch(err => {
						console.log(err);
						notie.error("Error loading user", err);
					});
			} else {
				return BB.props({
					client: TenantService.getClient("current"),
					contentAreas: ContentAreaService.listContentAreas(),
					districts: DistrictService.listDistricts(),
					grades: GradeService.listGrades()
				})
					.then(resps => {
						this.roles = resps.client.data.roles;

						this.sAreas = [];
						this.areas = resps.contentAreas.data.content_areas;

						this.sGrades = [];
						this.grades = resps.grades.data;

						this.districts = resps.districts.data.districts;

						this.editUser = this.blankUser();
						this.editUser.loaded = loaded;
						this.editUser.first_name = "New";
						this.editUser.last_name = "User";
						this.watchChanges();
					})
					.catch(err => {
						console.log(err);
						notie.error("Error loading user", err);
					});
			}
		},

		showValidationHints() {
			this.validationHints = true;
		},

		getValidationErrors() {
			let errors = [];
			_.each(this.validators, va => {
				if (!va.func()) {
					errors.push(va.errorMsg);
				}
			});
			return errors;
		},

		saveUser() {
			var dontSendTeams = this.user.teams;
			this.user.teams = null;
			let saveUser = _.cloneDeep(this.editUser);
			delete saveUser.client;
			delete saveUser.role;
			return UserService.saveUser(saveUser)
				.then(resp => {
					notie.info("User Saved");
					if (this.editUser.id != resp.data.id) {
						this.$router.replace(`/users/${resp.data.id}`);
					}
					this.loadData();
				})
				.catch(err => {
					console.log(err.response);
					notie.error(`Failed to save user`, err);
				});
			this.user.teams = dontSendTeams;
		},

		canSave() {
			return this.dirty;
		},

		togglePass() {
			if (this.editUser.setPass) {
				this.$set(this.editUser, "setPass", false);
				this.$set(this.editUser, "password", null);
				this.$set(this.editUser, "password2", null);
			} else {
				this.$set(this.editUser, "setPass", true);
				this.$set(this.editUser, "password", "");
				this.$set(this.editUser, "password2", "");
			}
		}
	}
};
</script>



// WEBPACK FOOTER //
// src/vues/UserMgmt/UserEdit.vue